import { format, formatDistanceToNow, isToday, isTomorrow, isPast } from 'date-fns';

/**
 * Safely convert any timestamp format to a JS Date.
 * Handles: Firestore Timestamp (.toDate), serialized Timestamp ({seconds}),
 * ISO strings, epoch numbers, and Date objects.
 */
function toDate(timestamp) {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp.toDate === 'function') return timestamp.toDate();
  // Serialized Firestore Timestamp (e.g. passed through React Router state)
  if (typeof timestamp === 'object' && typeof timestamp.seconds === 'number') {
    return new Date(timestamp.seconds * 1000);
  }
  const d = new Date(timestamp);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Format a game start time for display
 * e.g., "Today at 7:30 PM", "Tomorrow at 1:00 PM", "Feb 15 at 4:05 PM"
 */
export function formatGameTime(timestamp) {
  if (!timestamp) return 'TBD';

  const date = toDate(timestamp);
  if (!date) return 'TBD';

  if (isToday(date)) {
    return `Today at ${format(date, 'h:mm a')}`;
  }
  if (isTomorrow(date)) {
    return `Tomorrow at ${format(date, 'h:mm a')}`;
  }
  return format(date, 'MMM d \'at\' h:mm a');
}

/**
 * Format a relative time string
 * e.g., "in 2 hours", "3 days ago"
 */
export function formatRelativeTime(timestamp) {
  if (!timestamp) return '';
  const date = toDate(timestamp);
  if (!date) return '';
  return formatDistanceToNow(date, { addSuffix: true });
}

/**
 * Check if a game has started (start time is in the past)
 */
export function hasGameStarted(startTime) {
  if (!startTime) return false;
  const date = toDate(startTime);
  if (!date) return false;
  return isPast(date);
}

/**
 * Format a streak number for display
 */
export function formatStreak(streak) {
  if (streak === 0) return '0';
  return `🔥 ${streak}`;
}

/**
 * Format a date for pick history
 * e.g., "Feb 5, 2026 at 7:30 PM"
 */
export function formatPickDate(timestamp) {
  if (!timestamp) return '';
  const date = toDate(timestamp);
  if (!date) return '';
  return format(date, 'MMM d, yyyy \'at\' h:mm a');
}

/**
 * Get a display label for pick status
 */
export function getPickStatusLabel(status) {
  const labels = {
    pending: 'Pending',
    locked: 'Locked — Game in Progress',
    won: 'Correct ✅',
    lost: 'Incorrect ❌',
    push: 'Push (Draw)',
    cancelled: 'Cancelled',
  };
  return labels[status] || status;
}

/**
 * Get the next 3:00 AM ET as a Date (UTC).
 * Used for the "new games in..." countdown.
 */
export function getNext3amET() {
  const nowUTC = new Date();
  const etString = nowUTC.toLocaleString('en-US', { timeZone: 'America/New_York' });
  const etNow = new Date(etString);

  const target = new Date(etNow);
  target.setHours(3, 0, 0, 0);

  if (etNow >= target) {
    target.setDate(target.getDate() + 1);
  }

  const diffMs = target.getTime() - etNow.getTime();
  return new Date(nowUTC.getTime() + diffMs);
}
